import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
// Centralized CSS imports
import "./App.css";
import "./components/Sidebar.css";
import "./components/CodeEditor.css";
import "./components/WelcomeScreen.css";
import "./components/AnalysisResults.css";
import "./components/AIAssistant.css";
import "./components/Auth.css";
import "./components/DependencyAnalysis.css";
import "./components/KnowledgeGraph.css";

import FileExplorer from "./components/FileExplorer";
import CodeEditor from "./components/CodeEditor";
import AnalysisResults from "./components/AnalysisResults";
import FileUpload from "./components/FileUpload";
import AIAssistant from "./components/AIAssistant";
import { AuthProvider, useAuth } from "./components/AuthProvider";
import AuthModal from "./components/AuthModal";
import DependencyAnalysisTab from "./components/DependencyAnalysisTab";

// API Configuration
// Returns REACT_APP_API_URL if set, otherwise empty string for relative paths (nginx proxy mode)
const getApiUrl = () => {
  return process.env.REACT_APP_API_URL || "";
};

function AppContent() {
  const { user, isAuthenticated, isLoading: authLoading, logout } = useAuth();
  const [sessionId, setSessionId] = useState(null);
  const [files, setFiles] = useState({});
  const [originalFiles, setOriginalFiles] = useState({}); // Add originalFiles state
  const [fileTree, setFileTree] = useState({});
  const [analysisResults, setAnalysisResults] = useState(null);
  const [isImpactAnalyzing, setIsImpactAnalyzing] = useState(false);
  const [isDependencyAnalyzing, setIsDependencyAnalyzing] = useState(false);
  const [isUpdatingDocument, setIsUpdatingDocument] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(300); // Default width
  const [aiPanelCollapsed, setAiPanelCollapsed] = useState(false);
  const [aiPanelWidth, setAiPanelWidth] = useState(400); // Default AI panel width
  const [aiOverlayOpen, setAiOverlayOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [floatingButtonClosing, setFloatingButtonClosing] = useState(false);

  // Refs for sidebar resizing
  const isResizing = useRef(false);
  const sidebarRef = useRef(null);

  // Refs for AI panel resizing
  const isResizingAiPanel = useRef(false);
  const aiPanelRef = useRef(null);

  // Convert to Python state
  const [convertDropdownOpen, setConvertDropdownOpen] = useState(false);
  const [selectedFilesForConversion, setSelectedFilesForConversion] = useState(
    []
  );
  const [isConverting, setIsConverting] = useState(false);
  const [currentPage, setCurrentPage] = useState("pli"); // 'pli', 'python', or 'playwright'
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [pythonFiles, setPythonFiles] = useState({});
  const [pythonFileTree, setPythonFileTree] = useState({});
  const [playwrightFiles, setPlaywrightFiles] = useState({});
  const [playwrightFileTree, setPlaywrightFileTree] = useState({});

  // Separate state for each editor
  const [pliTabs, setPliTabs] = useState([]);
  const [pythonTabs, setPythonTabs] = useState([]);
  const [pliActiveTab, setPliActiveTab] = useState(null);
  const [pythonActiveTab, setPythonActiveTab] = useState(null);
  const [pliSelectedFile, setPliSelectedFile] = useState(null);
  const [pythonSelectedFile, setPythonSelectedFile] = useState(null);
  const [playwrightTabs, setPlaywrightTabs] = useState([]);
  const [playwrightActiveTab, setPlaywrightActiveTab] = useState(null);
  const [playwrightSelectedFile, setPlaywrightSelectedFile] = useState(null);

  // AI Assistant chat state - shared between sidebar and full-screen
  const [chatHistory, setChatHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAvailable, setIsAvailable] = useState(true);

  // Constant for empty originalFiles (prevents creating new object on each render)
  const EMPTY_ORIGINAL_FILES = useMemo(() => ({}), []);

  // Memoized calculations for AIAssistant props
  const aiAssistantSelectedFile = useMemo(() => {
    if (currentPage === "pli") return pliSelectedFile;
    if (currentPage === "python") return pythonSelectedFile;
    return playwrightSelectedFile;
  }, [currentPage, pliSelectedFile, pythonSelectedFile, playwrightSelectedFile]);

  const aiAssistantFileContent = useMemo(() => {
    if (currentPage === "pli") {
      return pliTabs.find((tab) => tab.id === pliActiveTab)?.content || "";
    } else if (currentPage === "python") {
      return pythonTabs.find((tab) => tab.id === pythonActiveTab)?.content || "";
    } else {
      return playwrightTabs.find((tab) => tab.id === playwrightActiveTab)?.content || "";
    }
  }, [currentPage, pliTabs, pliActiveTab, pythonTabs, pythonActiveTab, playwrightTabs, playwrightActiveTab]);

  const aiAssistantFiles = useMemo(() => {
    if (currentPage === "pli") return files;
    if (currentPage === "python") return pythonFiles;
    return playwrightFiles;
  }, [currentPage, files, pythonFiles, playwrightFiles]);

  const aiAssistantOriginalFiles = useMemo(() => {
    if (currentPage === "pli") return originalFiles;
    return EMPTY_ORIGINAL_FILES;
  }, [currentPage, originalFiles, EMPTY_ORIGINAL_FILES]);

  const handleCollapseAiPanel = useCallback(() => {
    setAiPanelCollapsed(true);
  }, []);

  // Helper functions to get current editor state
  const getCurrentTabs = () => {
    if (currentPage === "pli") return pliTabs;
    if (currentPage === "python") return pythonTabs;
    return playwrightTabs;
  };
  const getCurrentActiveTab = () => {
    if (currentPage === "pli") return pliActiveTab;
    if (currentPage === "python") return pythonActiveTab;
    return playwrightActiveTab;
  };
  const getCurrentSelectedFile = () => {
    if (currentPage === "pli") return pliSelectedFile;
    if (currentPage === "python") return pythonSelectedFile;
    return playwrightSelectedFile;
  };

  const setCurrentTabs = (newTabs) => {
    if (currentPage === "pli") {
      setPliTabs(newTabs);
    } else if (currentPage === "python") {
      setPythonTabs(newTabs);
    } else {
      setPlaywrightTabs(newTabs);
    }
  };

  const setCurrentActiveTab = (tabId) => {
    if (currentPage === "pli") {
      setPliActiveTab(tabId);
    } else if (currentPage === "python") {
      setPythonActiveTab(tabId);
    } else {
      setPlaywrightActiveTab(tabId);
    }
  };

  const setCurrentSelectedFile = (filePath) => {
    if (currentPage === "pli") {
      setPliSelectedFile(filePath);
    } else if (currentPage === "python") {
      setPythonSelectedFile(filePath);
    } else {
      setPlaywrightSelectedFile(filePath);
    }
  };

  // Show auth modal if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      setAuthModalOpen(true);
    }
  }, [authLoading, isAuthenticated]);

  // Sidebar resize handlers
  const startResizing = useCallback(() => {
    isResizing.current = true;
    document.body.classList.add("resizing");
  }, []);

  const stopResizing = useCallback(() => {
    isResizing.current = false;
    document.body.classList.remove("resizing");
  }, []);

  const resize = useCallback((mouseMoveEvent) => {
    if (isResizing.current && sidebarRef.current) {
      const newWidth =
        mouseMoveEvent.clientX -
        sidebarRef.current.getBoundingClientRect().left;

      // Set min and max width constraints
      const minWidth = 200;
      const maxWidth = 600;

      if (newWidth >= minWidth && newWidth <= maxWidth) {
        setSidebarWidth(newWidth);
      }
    }
  }, []);

  // AI Panel resize handlers
  const startResizingAiPanel = useCallback(() => {
    isResizingAiPanel.current = true;
    document.body.classList.add("resizing");
  }, []);

  const stopResizingAiPanel = useCallback(() => {
    isResizingAiPanel.current = false;
    document.body.classList.remove("resizing");
  }, []);

  const resizeAiPanel = useCallback((mouseMoveEvent) => {
    if (isResizingAiPanel.current && aiPanelRef.current) {
      const panelRect = aiPanelRef.current.getBoundingClientRect();
      const newWidth = panelRect.right - mouseMoveEvent.clientX;

      // Set min and max width constraints for AI panel
      const minWidth = 300;
      const maxWidth = 800;

      if (newWidth >= minWidth && newWidth <= maxWidth) {
        setAiPanelWidth(newWidth);
      }
    }
  }, []);

  // Add event listeners for resizing (sidebar and AI panel)
  useEffect(() => {
    document.addEventListener("mousemove", resize);
    document.addEventListener("mouseup", stopResizing);
    document.addEventListener("mousemove", resizeAiPanel);
    document.addEventListener("mouseup", stopResizingAiPanel);

    return () => {
      document.removeEventListener("mousemove", resize);
      document.removeEventListener("mouseup", stopResizing);
      document.removeEventListener("mousemove", resizeAiPanel);
      document.removeEventListener("mouseup", stopResizingAiPanel);
    };
  }, [resize, stopResizing, resizeAiPanel, stopResizingAiPanel]);

  const loadSessionFiles = useCallback(async () => {
    try {
      const response = await fetch(
        `${getApiUrl()}/api/sessions/${sessionId}/files`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setFiles(data.files);
      setOriginalFiles(data.original_files); // Load original files from backend
      setFileTree(data.file_tree);
    } catch (error) {
      console.error("Failed to load session files:", error);
    }
  }, [sessionId]);

  // Load session data when sessionId changes
  useEffect(() => {
    if (sessionId) {
      loadSessionFiles();
    }
  }, [sessionId, loadSessionFiles]);

  const handleFileUpload = (uploadData) => {
    setSessionId(uploadData.session_id);
    setFiles(uploadData.files);
    setOriginalFiles(uploadData.files); // Store original files on upload
    setFileTree(uploadData.file_tree);
  };

  const handleFileSelect = async (filePath) => {
    const currentTabs = getCurrentTabs();

    // Check if file is already open in a tab
    const existingTab = currentTabs.find((tab) => tab.path === filePath);
    if (existingTab) {
      // File is already open, just switch to that tab
      setCurrentActiveTab(existingTab.id);
      setCurrentSelectedFile(filePath);
      return;
    }

    try {

      // For Python files, get content from local state
      if (currentPage === "python" && pythonFiles[filePath]) {
        const newTab = {
          id: Date.now(),
          name: filePath.split("/").pop(),
          path: filePath,
          content: pythonFiles[filePath].python_code || "",
          modified: false,
          isPython: true,
        };

        setCurrentTabs([...currentTabs, newTab]);
        setCurrentActiveTab(newTab.id);
        setCurrentSelectedFile(filePath);
        return;
      }

      // For Playwright files, get content from local state
      if (currentPage === "playwright" && playwrightFiles[filePath]) {
        const newTab = {
          id: Date.now(),
          name: filePath.split("/").pop(),
          path: filePath,
          content: playwrightFiles[filePath].playwright_code || "",
          modified: false,
          isPlaywright: true,
        };

        setCurrentTabs([...currentTabs, newTab]);
        setCurrentActiveTab(newTab.id);
        setCurrentSelectedFile(filePath);
        return;
      }

      // For PL/I files, fetch from backend
      const response = await fetch(
        `${getApiUrl()}/api/sessions/${sessionId}/files/${filePath}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Create new tab for the file
      const newTab = {
        id: Date.now(),
        name: filePath.split("/").pop(),
        path: filePath,
        content: data.content || "",
        modified: false,
        isPython: currentPage === "python",
        isPlaywright: currentPage === "playwright",
      };

      // Add to tabs and make it active
      setCurrentTabs([...currentTabs, newTab]);
      setCurrentActiveTab(newTab.id);
      setCurrentSelectedFile(filePath);
    } catch (error) {
      console.error("Failed to load file:", error);
      alert(`Failed to load file: ${error.message}`);
    }
  };

  const handleFileSave = async (filePath, content) => {
    try {

      const response = await fetch(
        `${getApiUrl()}/api/sessions/${sessionId}/files/${filePath}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ content }),
        }
      );

      const data = await response.json();

      // Update tabs with new content and mark as not modified
      const currentTabs = getCurrentTabs();
      setCurrentTabs(
        currentTabs.map((tab) =>
          tab.path === filePath ? { ...tab, content, modified: false } : tab
        )
      );

      // Reload files to get updated data
      await loadSessionFiles();

      return data;
    } catch (error) {
      console.error("Failed to save file:", error);
      return null;
    }
  };

  const handleTabClose = useCallback(
    (tabId) => {
      const currentTabs = getCurrentTabs();
      const newTabs = currentTabs.filter((tab) => tab.id !== tabId);
      setCurrentTabs(newTabs);

      const currentActiveTab = getCurrentActiveTab();
      if (currentActiveTab === tabId) {
        if (newTabs.length > 0) {
          // Find the next tab to activate (prefer the one to the right, then left)
          const currentIndex = currentTabs.findIndex((tab) => tab.id === tabId);
          let nextTab;

          if (currentIndex < newTabs.length) {
            // If there's a tab to the right, use it
            nextTab = newTabs[currentIndex];
          } else {
            // Otherwise, use the last tab
            nextTab = newTabs[newTabs.length - 1];
          }

          setCurrentActiveTab(nextTab.id);
          setCurrentSelectedFile(nextTab.path);
        } else {
          setCurrentActiveTab(null);
          setCurrentSelectedFile(null);
        }
      }
    },
    [currentPage, pliTabs, pythonTabs, playwrightTabs, pliActiveTab, pythonActiveTab, playwrightActiveTab]
  );

  const handleRunAnalysis = async () => {
    if (!sessionId) return;

    // Start impact analysis
    setIsImpactAnalyzing(true);
    try {
      const response = await fetch(
        `${getApiUrl()}/api/sessions/${sessionId}/analyze`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            convert_to_python: false,
          }),
        }
      );

      const data = await response.json();
      // Ensure knowledge graph data is included in analysis results
      const analysisData = data.analysis_results || {};

      // Debug: Log the full response to see what we're getting

      // If knowledge graph data is not included in the response, add empty placeholders
      if (!analysisData.old_kg_triplets) {
        analysisData.old_kg_triplets = [];
      }
      if (!analysisData.new_kg_triplets) {
        analysisData.new_kg_triplets = [];
      }
      if (!analysisData.kg_comparison) {
        analysisData.kg_comparison = {};
      }

      // IMPORTANT: Use instant_impact_explanation data which contains pre-extracted log/logic classification
      if (
        data.instant_impact_explanation &&
        data.instant_impact_explanation.detailed_explanations
      ) {

        // Convert instant_impact_explanation format to match expected impact_analysis format
        analysisData.impact_analysis =
          data.instant_impact_explanation.detailed_explanations.map(
            (explanation) => ({
              function_name: explanation.function_name,
              file_name: explanation.file_name,
              impact_level:
                explanation.risk_level === "high"
                  ? "Logic Impact"
                  : explanation.risk_level === "medium"
                  ? "Log Impact"
                  : "No Impact",
              confidence:
                explanation.risk_level === "high"
                  ? 0.9
                  : explanation.risk_level === "medium"
                  ? 0.7
                  : 0.5,
              reasoning: explanation.explanation_text,
              affected_functions: [
                ...explanation.upstream_impact_details.map(
                  (item) => item.function
                ),
                ...explanation.downstream_impact_details.map(
                  (item) => item.function
                ),
              ],
              impact_classification: explanation.impact_classification, // This contains the pre-extracted log/logic data!
              change_summary: explanation.risk_summary,
              risk_factors: [explanation.risk_level],
            })
          );

        // Also add summary from instant impact
        analysisData.summary = {
          total_functions:
            data.instant_impact_explanation.total_impacted_functions || 0,
          logic_impact:
            data.instant_impact_explanation.risk_assessment?.high || 0,
          log_impact:
            data.instant_impact_explanation.risk_assessment?.medium || 0,
          no_impact: data.instant_impact_explanation.risk_assessment?.low || 0,
        };

      }

      setAnalysisResults(analysisData);

      // Open analysis results in new tab
      // IMPORTANT: Use the modified analysisData (not data.analysis_results) so summary data is included
      const analysisTab = {
        id: Date.now(),
        name: "Analysis Results",
        path: "analysis_results",
        content: analysisData, // Use modified analysisData which includes impact_summary and paragraph_summary
        modified: false,
        isAnalysis: true,
      };
      const currentTabs = getCurrentTabs();
      setCurrentTabs([...currentTabs, analysisTab]);
      setCurrentActiveTab(analysisTab.id);
    } catch (error) {
      console.error("Analysis failed:", error);
    } finally {
      setIsImpactAnalyzing(false);
    }
  };

  const handleRunDependencyAnalysis = async () => {
    if (!sessionId) return;

    // Start dependency analysis
    setIsDependencyAnalyzing(true);
    try {

      // First check if analysis already exists
      let response = await fetch(
        `${getApiUrl()}/api/sessions/${sessionId}/dependency-analysis`,
        {
          method: "GET",
          credentials: "include",
        }
      );


      let data;
      if (response.ok) {
        try {
          // Check if response has content
          const contentType = response.headers.get("content-type");
          if (!contentType || !contentType.includes("application/json")) {
            const text = await response.text();
            throw new Error(`Invalid response type. Expected JSON, got: ${contentType}. Response: ${text.substring(0, 200)}`);
          }

          // Get response text first to check if it's complete
          const responseText = await response.text();
          if (!responseText || responseText.trim().length === 0) {
            throw new Error("Empty response received from server");
          }

          // Try to parse JSON
          try {
            data = JSON.parse(responseText);
          } catch (jsonError) {
            console.error("JSON parsing error:", jsonError);
            console.error("Response length:", responseText.length);
            console.error("Response preview:", responseText.substring(0, 500));
            throw new Error(`Failed to parse JSON response: ${jsonError.message}. Response may be incomplete or corrupted.`);
          }

        } catch (parseError) {
          if (parseError.message.includes("JSON") || parseError.message.includes("parse")) {
            throw new Error(`JSON parsing failed: ${parseError.message}. The response may be too large or incomplete.`);
          }
          throw parseError;
        }

        // Check if analysis_results is null - if so, we need to run a new analysis
        if (!data.analysis_results || data.analysis_results === null) {
          response = await fetch(
            `${getApiUrl()}/api/sessions/${sessionId}/dependency-analysis`,
            {
              method: "POST",
              credentials: "include",
            }
          );

          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(
              `Dependency analysis failed: ${response.status} ${response.statusText} - ${errorText}`
            );
          }

          try {
            const responseText = await response.text();
            if (!responseText || responseText.trim().length === 0) {
              throw new Error("Empty response received from server");
            }
            const responseSizeMB = (responseText.length / 1024 / 1024).toFixed(2);
            console.log(`Response size: ${responseSizeMB} MB`);
            data = JSON.parse(responseText);
          } catch (jsonError) {
            console.error("JSON parsing error on POST:", jsonError);
            throw new Error(`Failed to parse JSON response: ${jsonError.message}. The analysis result may be too large.`);
          }
        }
      } else {
        // If no existing analysis, run new one
        response = await fetch(
          `${getApiUrl()}/api/sessions/${sessionId}/dependency-analysis`,
          {
            method: "POST",
            credentials: "include",
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(
            `Dependency analysis failed: ${response.status} ${response.statusText} - ${errorText}`
          );
        }

        try {
          const responseText = await response.text();
          if (!responseText || responseText.trim().length === 0) {
            throw new Error("Empty response received from server");
          }
          const responseSizeMB = (responseText.length / 1024 / 1024).toFixed(2);
          console.log(`Response size: ${responseSizeMB} MB`);
          data = JSON.parse(responseText);
        } catch (jsonError) {
          console.error("JSON parsing error on POST:", jsonError);
          throw new Error(`Failed to parse JSON response: ${jsonError.message}. The analysis result may be too large.`);
        }
      }

      // Extract analysis results from the response
      const analysisResults = data.analysis_results || data;

      // Final validation
      if (!analysisResults || analysisResults === null) {
        throw new Error(
          "Analysis returned null results. Please ensure you are logged in and try again."
        );
      }

      // Open dependency analysis results in new tab
      const dependencyTab = {
        id: Date.now(),
        name: "Correlation",
        path: "dependency_analysis",
        content: analysisResults,
        modified: false,
        isDependencyAnalysis: true,
      };

      const currentTabs = getCurrentTabs();
      setCurrentTabs([...currentTabs, dependencyTab]);
      setCurrentActiveTab(dependencyTab.id); // Focus on Dependency Analysis by default
    } catch (error) {
      console.error("Dependency analysis failed:", error);
      console.error("Error details:", {
        message: error.message,
        stack: error.stack,
        name: error.name
      });

      // Check if it's an authentication error
      if (
        error.message.includes("401") ||
        error.message.includes("Authentication required")
      ) {
        alert(
          "❌ Authentication Required\n\nYou must be logged in to run dependency analysis.\n\nPlease log in first, then try again."
        );
      } else if (error.message.includes("JSON") || error.message.includes("parse") || error.message.includes("Unexpected end")) {
        // JSON parsing error - likely response too large
        alert(
          `❌ Dependency Analysis Failed\n\n${error.message}\n\nThis may be due to:\n1. Response is too large (analysis contains many files/functions)\n2. Network timeout\n3. Browser memory limit\n\nPlease try:\n1. Check browser console for details\n2. Try with fewer files\n3. Check backend logs for errors\n4. Ensure backend is running and accessible`
        );
      } else {
        alert(
          `❌ Dependency Analysis Failed\n\n${error.message}\n\nPlease check:\n1. You are logged in\n2. Files are uploaded\n3. Backend is running and accessible\n4. Check browser console for more details`
        );
      }
    } finally {
      setIsDependencyAnalyzing(false);
    }
  };

  const handleUpdateDocument = async () => {
    if (!sessionId) return;

    // Start document update
    setIsUpdatingDocument(true);
    try {
      const response = await fetch(
        `${getApiUrl()}/api/sessions/${sessionId}/update-document`,
        {
          method: "POST",
          credentials: "include",
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Document update failed: ${response.status} ${response.statusText} - ${errorText}`
        );
      }

      // Get the blob from response
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get("Content-Disposition");
      let filename = "updated_document.docx";
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      alert("✅ Document updated successfully! The updated file has been downloaded.");
    } catch (error) {
      console.error("Document update failed:", error);

      // Check if it's an authentication error
      if (
        error.message.includes("401") ||
        error.message.includes("Authentication required")
      ) {
        alert(
          "❌ Authentication Required\n\nYou must be logged in to update documents.\n\nPlease log in first, then try again."
        );
      } else if (error.message.includes("404") && error.message.includes(".docx")) {
        alert(
          "❌ Document Not Found\n\nNo .docx file found in the uploaded files.\n\nPlease upload a ZIP file containing a .docx file along with your code files."
        );
      } else {
        alert(
          `❌ Document Update Failed\n\n${error.message}\n\nPlease check:\n1. You are logged in\n2. A .docx file is included in your uploaded ZIP\n3. Code files are present\n4. Backend is running`
        );
      }
    } finally {
      setIsUpdatingDocument(false);
    }
  };

  const handleOpenAiOverlay = useCallback(() => {
    setAiOverlayOpen(true);
  }, []);

  const handleCloseAiOverlay = () => {
    setAiOverlayOpen(false);
  };

  const handleOpenAiPanelFromFloatingButton = () => {
    setFloatingButtonClosing(true);
    // Wait for the animation to complete before opening the panel
    setTimeout(() => {
      setAiPanelCollapsed(false);
      setFloatingButtonClosing(false);
    }, 350); // Slightly less than animation duration (400ms)
  };

  // Convert to Python functions
  const getPLIFiles = () => {
    const pliFiles = [];
    const visited = new Set(); // Prevent infinite recursion


    // First, try to get PL/I files from the files state directly
    if (files && typeof files === "object") {
      Object.keys(files).forEach((filePath) => {
        const lowerPath = filePath.toLowerCase();
        // Check for various PL/I file extensions
        if (
          lowerPath.endsWith(".pli") ||
          lowerPath.endsWith(".pl1") ||
          lowerPath.endsWith(".pl/i")
        ) {
          pliFiles.push(filePath);
        }
      });
    }

    // If no files found in files state, try traversing the file tree
    if (pliFiles.length === 0) {
      const traverseTree = (tree, path = "") => {
        // Safety check to prevent infinite recursion
        if (!tree || typeof tree !== "object" || visited.has(tree)) {
          return;
        }

        visited.add(tree);

        Object.keys(tree).forEach((key) => {
          const fullPath = path ? `${path}/${key}` : key;
          const item = tree[key];


          // Check if it's a folder (has type property or children)
          if (
            item &&
            typeof item === "object" &&
            (item.type === "folder" || item.children)
          ) {
            // It's a directory - recursively traverse
            const children = item.children || item;
            traverseTree(children, fullPath);
          } else {
            // It's a file (no type property or children)
            const lowerKey = key.toLowerCase();
            const isPLIFile =
              lowerKey.endsWith(".pli") ||
              lowerKey.endsWith(".pl1") ||
              lowerKey.endsWith(".pl/i");
            if (isPLIFile) {
              pliFiles.push(fullPath);
            }
          }
        });
      };

      if (fileTree && typeof fileTree === "object") {
        traverseTree(fileTree);
      }
    }

    return pliFiles;
  };

  const handleFileSelectionChange = (filePath, isSelected) => {
    if (isSelected) {
      setSelectedFilesForConversion([...selectedFilesForConversion, filePath]);
    } else {
      setSelectedFilesForConversion(
        selectedFilesForConversion.filter((f) => f !== filePath)
      );
    }
  };

  const handleConvertToPython = async () => {
    if (selectedFilesForConversion.length === 0) {
      alert("Please select at least one PL/I or VB6 file to convert.");
      return;
    }

    setIsConverting(true);
    setConvertDropdownOpen(false);

    try {
      const response = await fetch(
        `${getApiUrl()}/api/sessions/${sessionId}/convert-to-python`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            files: selectedFilesForConversion,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Store converted Python files
      setPythonFiles(data.converted_files || {});
      setPythonFileTree(data.file_tree || {});

      // Switch to Python page automatically
      setCurrentPage("python");
      setSelectedFilesForConversion([]);

      // Don't clear tabs - preserve existing state
    } catch (error) {
      console.error("Conversion failed:", error);
      alert(`Conversion failed: ${error.message}`);
    } finally {
      setIsConverting(false);
    }
  };

  const handleConvertToPlaywright = async () => {
    if (selectedFilesForConversion.length === 0) {
      alert("Please select at least one Coded UI file to convert.");
      return;
    }

    setIsConverting(true);
    setConvertDropdownOpen(false);

    try {
      const response = await fetch(
        `${getApiUrl()}/api/sessions/${sessionId}/convert-to-playwright`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            files: selectedFilesForConversion,
          }),
        }
      );

      if (!response.ok) {
        let detail = `HTTP ${response.status}`;
        try {
          const err = await response.json();
          if (err && err.detail) detail = err.detail;
        } catch (_) {}
        throw new Error(detail);
      }

      const data = await response.json();

      // Store converted Playwright files
      setPlaywrightFiles(data.converted_files || {});
      setPlaywrightFileTree(data.file_tree || {});

      // Switch to Playwright page automatically
      setCurrentPage("playwright");
      setSelectedFilesForConversion([]);
    } catch (error) {
      console.error("Playwright conversion failed:", error);
      alert(`Conversion failed: ${error.message}`);
    } finally {
      setIsConverting(false);
    }
  };

  const handleDownloadFile = (filename, fileType) => {
    const files = fileType === "python" ? pythonFiles : playwrightFiles;
    const codeKey = fileType === "python" ? "python_code" : "playwright_code";

    // Try exact match first, then try matching by basename
    let fileData = files[filename];
    if (!fileData) {
      const basename = filename.split("/").pop().split("\\").pop();
      fileData = Object.entries(files).find(([key]) =>
        key === basename || key.endsWith("/" + basename) || key.endsWith("\\" + basename)
      )?.[1];
    }

    const content = fileData?.[codeKey] || fileData?.content || "";
    if (!content) {
      alert(`No content found for file: ${filename}`);
      return;
    }

    const downloadName = filename.split("/").pop().split("\\").pop();
    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = downloadName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDownloadAllAsZip = async (fileType) => {
    try {
      const response = await fetch(
        `/api/sessions/${sessionId}/download-all/${fileType}`,
        { credentials: 'include' }
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileType === "python" ? "converted_python_files.zip" : "converted_playwright_files.zip";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
      alert(`Download failed: ${error.message}`);
    }
  };

  const loadPythonFiles = useCallback(async () => {
    if (!sessionId) return;

    try {
      const response = await fetch(
        `${getApiUrl()}/api/sessions/${sessionId}/python-files`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setPythonFiles(data.python_files || {});
      setPythonFileTree(data.python_file_tree || {});
    } catch (error) {
      console.error("Failed to load Python files:", error);
    }
  }, [sessionId, currentPage]);

  const loadPlaywrightFiles = useCallback(async () => {
    if (!sessionId) return;

    try {
      const response = await fetch(
        `${getApiUrl()}/api/sessions/${sessionId}/playwright-files`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setPlaywrightFiles(data.playwright_files || {});
      setPlaywrightFileTree(data.playwright_file_tree || {});
    } catch (error) {
      console.error("Failed to load Playwright files:", error);
    }
  }, [sessionId, currentPage]);

  const handleSwitchEditor = () => {
    // Cycle through: pli -> python -> playwright -> pli
    let newPage;
    if (currentPage === "pli") {
      newPage = "python";
    } else if (currentPage === "python") {
      newPage = "playwright";
    } else {
      newPage = "pli";
    }
    setCurrentPage(newPage);

    // Load files when switching to converted pages
    if (newPage === "python") {
      loadPythonFiles();
    } else if (newPage === "playwright") {
      loadPlaywrightFiles();
    }
  };

  // AI Assistant functions - shared between sidebar and full-screen
  const checkAIAvailability = async () => {
    try {
      const apiUrl = getApiUrl();
      await fetch(`${apiUrl}/api/health`);
      setIsAvailable(true);
    } catch (error) {
      console.warn("AI service not available:", error);
      setIsAvailable(false);
    }
  };

  const handleSendMessage = useCallback(async (message) => {
    if (!message || !message.trim() || !sessionId) return;

    const userMessage = {
      role: "user",
      content: message,
      timestamp: new Date().toISOString(),
    };

    setChatHistory((prev) => [...prev, userMessage]);
    setIsLoading(true);

    try {
      // Get content from active tab based on current page
      let activeTabData,
        currentFileContent,
        currentSelectedFile,
        currentFiles,
        currentOriginalFiles;

      if (currentPage === "pli") {
        activeTabData = pliTabs.find((tab) => tab.id === pliActiveTab);
        currentFileContent = activeTabData?.content || "";
        currentSelectedFile = pliSelectedFile;
        currentFiles = files;
        currentOriginalFiles = originalFiles;
      } else {
        activeTabData = pythonTabs.find((tab) => tab.id === pythonActiveTab);
        currentFileContent = activeTabData?.content || "";
        currentSelectedFile = pythonSelectedFile;
        currentFiles = pythonFiles;
        currentOriginalFiles = {}; // Python files don't have original files concept
      }

      // Prepare context for AI
      const context = {
        sessionId,
        selectedFile: currentSelectedFile,
        fileContent: currentFileContent,
        files: currentFiles || {},
        originalFiles: currentOriginalFiles || {},
        analysisResults: analysisResults || null,
        userQuery: message,
        // Add original file content for the selected file
        originalFileContent:
          currentSelectedFile &&
          currentOriginalFiles &&
          currentOriginalFiles[currentSelectedFile]
            ? currentOriginalFiles[currentSelectedFile].content
            : "",
      };


      // Call AI analysis endpoint
      const response = await fetch(
        `${getApiUrl()}/api/sessions/${sessionId}/ai-chat`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            message: message,
            context: context,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      const aiResponse = {
        role: "assistant",
        content:
          data.response ||
          "I received your message but could not generate a response.",
        timestamp: new Date().toISOString(),
        analysis: data.analysis || null,
      };

      setChatHistory((prev) => [...prev, aiResponse]);
    } catch (error) {
      console.error("AI request failed:", error);
      const errorResponse = {
        role: "assistant",
        content: `Sorry, I encountered an error: ${error.message}. Please check if the AI service is properly configured.`,
        timestamp: new Date().toISOString(),
      };
      setChatHistory((prev) => [...prev, errorResponse]);
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, currentPage, pliTabs, pliActiveTab, pliSelectedFile, files, originalFiles, pythonTabs, pythonActiveTab, pythonSelectedFile, pythonFiles, analysisResults]);

  const clearChat = useCallback(async () => {
    if (!sessionId) {
      setChatHistory([]);
      return;
    }

    try {
      // Clear chat on backend
      const response = await fetch(
        `${getApiUrl()}/api/sessions/${sessionId}/ai-chat/clear`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      // Chat history cleared on backend
    } catch (error) {
      // Silently handle error - frontend will clear chat anyway
    }

    // Clear chat on frontend
    setChatHistory([]);
  }, [sessionId]);

  // Check AI availability on component mount
  useEffect(() => {
    checkAIAvailability();
  }, []);

  // Handle keyboard shortcuts and click outside
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Handle escape key to close overlay or dropdown
      if (event.key === "Escape") {
        if (aiOverlayOpen) {
          handleCloseAiOverlay();
        } else if (convertDropdownOpen) {
          setConvertDropdownOpen(false);
        } else if (userDropdownOpen) {
          setUserDropdownOpen(false);
        }
        return;
      }

      // Handle tab navigation shortcuts
      if (event.ctrlKey || event.metaKey) {
        const currentTabs = getCurrentTabs();
        const currentActiveTab = getCurrentActiveTab();
        if (event.key === "Tab" && currentTabs.length > 1) {
          event.preventDefault();
          const currentIndex = currentTabs.findIndex((tab) => tab.id === currentActiveTab);
          if (event.shiftKey) {
            // Previous tab
            const prevIndex =
              currentIndex > 0 ? currentIndex - 1 : currentTabs.length - 1;
            const prevTab = currentTabs[prevIndex];
            setCurrentActiveTab(prevTab.id);
            setCurrentSelectedFile(prevTab.path);
          } else {
            // Next tab
            const nextIndex =
              currentIndex < currentTabs.length - 1 ? currentIndex + 1 : 0;
            const nextTab = currentTabs[nextIndex];
            setCurrentActiveTab(nextTab.id);
            setCurrentSelectedFile(nextTab.path);
          }
        } else if (event.key === "w" && currentActiveTab) {
          // Close current tab
          event.preventDefault();
          handleTabClose(currentActiveTab);
        }
      }
    };

    const handleClickOutside = (event) => {
      // Close dropdown if clicking outside
      if (
        convertDropdownOpen &&
        !event.target.closest(".convert-dropdown-container")
      ) {
        setConvertDropdownOpen(false);
      }
      if (userDropdownOpen && !event.target.closest(".user-info")) {
        setUserDropdownOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("click", handleClickOutside);

    if (aiOverlayOpen) {
      // Prevent body scroll when overlay is open
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("click", handleClickOutside);
      document.body.style.overflow = "unset";
    };
  }, [
    aiOverlayOpen,
    convertDropdownOpen,
    userDropdownOpen,
    getCurrentTabs,
    getCurrentActiveTab,
    setCurrentActiveTab,
    setCurrentSelectedFile,
    handleTabClose,
  ]);

  return (
    <div className="app">
      {/* Header */}
      <div className="header">
        <div className="header-left">
          <a href="/" className="home-link" title="Home">
            <img
              src="/coretix-logo.svg"
              alt="Coretix Logo"
              className="app-logo"
              style={{ height: "40px", marginLeft: "8px", cursor: "pointer" }}
            />
          </a>
          {/* <span className="app-title" style={{ marginLeft: "8px" }}>
          Impact Analyzer
            </span> */}
        </div>
        <div className="header-right">
          {/* View Switcher Buttons */}
          {sessionId && (
            <div style={{ display: "flex", gap: "4px" }}>
              <button
                className={`action-button switch-editor-button${currentPage === "pli" ? " active" : ""}`}
                onClick={() => setCurrentPage("pli")}
                style={{
                  background: currentPage === "pli" ? "#0d6efd" : undefined,
                  opacity: currentPage === "pli" ? 1 : 0.7,
                }}
                title="Source files"
              >
                Source
              </button>
              <button
                className={`action-button switch-editor-button${currentPage === "python" ? " active" : ""}`}
                onClick={() => { setCurrentPage("python"); loadPythonFiles(); }}
                style={{
                  background: currentPage === "python" ? "#0d6efd" : undefined,
                  opacity: currentPage === "python" ? 1 : 0.7,
                }}
                title="Converted Python files"
              >
                Python
              </button>
              <button
                className={`action-button switch-editor-button${currentPage === "playwright" ? " active" : ""}`}
                onClick={() => { setCurrentPage("playwright"); loadPlaywrightFiles(); }}
                style={{
                  background: currentPage === "playwright" ? "#2ea043" : undefined,
                  opacity: currentPage === "playwright" ? 1 : 0.7,
                }}
                title="Converted Playwright test files"
              >
                Playwright
              </button>
            </div>
          )}

          {/* Convert Buttons - Only show on Source page */}
          {sessionId && currentPage === "pli" && (
            <div className="convert-dropdown-container">
              <button
                className="action-button convert-python-button"
                onClick={() => setConvertDropdownOpen(!convertDropdownOpen)}
                disabled={isConverting}
                title="Convert source files to Python or Playwright"
              >
                {isConverting ? (
                  "Converting..."
                ) : (
                  <>
                    <svg
                      className="button-icon"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="16 18 22 12 16 6"></polyline>
                      <polyline points="8 6 2 12 8 18"></polyline>
                    </svg>
                    Convert
                  </>
                )}
              </button>

              {convertDropdownOpen && (
                <div className="convert-dropdown">
                  <div className="convert-dropdown-header">
                    <h4>Select files to convert:</h4>
                    <div className="convert-dropdown-header-buttons">
                      <button
                        className="convert-dropdown-select-all-btn"
                        onClick={() => {
                          const allFiles = files ? Object.keys(files) : [];
                          // Filter to only include convertible files
                          const fileLower = (path) => path.toLowerCase();
                          const convertibleFiles = allFiles.filter((filePath) => {
                            const lower = fileLower(filePath);
                            return (
                              lower.endsWith(".pli") ||
                              lower.endsWith(".pl1") ||
                              lower.endsWith(".pl/i") ||
                              lower.endsWith(".bas") ||
                              lower.endsWith(".frm") ||
                              lower.endsWith(".cls") ||
                              lower.endsWith(".ctl") ||
                              lower.endsWith(".dsr") ||
                              lower.endsWith(".pag") ||
                              lower.endsWith(".dob") ||
                              lower.endsWith(".cs") ||
                              lower.endsWith(".uitest") ||
                              lower.endsWith(".feature") ||
                              lower.endsWith(".cy.js") ||
                              lower.endsWith(".cy.ts") ||
                              lower.endsWith("_steps.js") ||
                              lower.endsWith("_steps.ts") ||
                              (lower.endsWith(".js") && !lower.endsWith(".config.js"))
                            );
                          });
                          setSelectedFilesForConversion(convertibleFiles);
                        }}
                      >
                        Select All
                      </button>
                      <button
                        className="convert-dropdown-clear-all-btn"
                        onClick={() => setSelectedFilesForConversion([])}
                      >
                        Clear All
                      </button>
                    </div>
                  </div>

                  <div className="convert-dropdown-files-container">
                    {(() => {
                      const pliFiles = getPLIFiles();
                      const allFiles = files ? Object.keys(files) : [];
                      const filesToShow = allFiles.length > 0 ? allFiles : pliFiles;

                      if (filesToShow.length === 0) {
                        return (
                          <div className="convert-dropdown-no-files">
                            No files found. Please upload files first.
                          </div>
                        );
                      }

                      return filesToShow.map((filePath) => {
                        const fileLower = filePath.toLowerCase();
                        const isPLIFile =
                          fileLower.endsWith(".pli") ||
                          fileLower.endsWith(".pl1") ||
                          fileLower.endsWith(".pl/i");
                        const isVB6File =
                          fileLower.endsWith(".bas") ||
                          fileLower.endsWith(".frm") ||
                          fileLower.endsWith(".cls") ||
                          fileLower.endsWith(".ctl") ||
                          fileLower.endsWith(".dsr") ||
                          fileLower.endsWith(".pag") ||
                          fileLower.endsWith(".dob");
                        const isCodedUIFile =
                          fileLower.endsWith(".cs") ||
                          fileLower.endsWith(".uitest");
                        const isCypressFile =
                          fileLower.endsWith(".cy.js") ||
                          fileLower.endsWith(".cy.ts") ||
                          fileLower.endsWith("_steps.js") ||
                          fileLower.endsWith("_steps.ts") ||
                          fileLower.endsWith(".feature") ||
                          (fileLower.endsWith(".js") && !fileLower.endsWith(".config.js"));
                        const isConvertibleFile = isPLIFile || isVB6File || isCodedUIFile || isCypressFile;

                        return (
                          <div
                            key={filePath}
                            className={`convert-dropdown-file-item ${
                              isConvertibleFile ? "pli-file" : "non-pli-file"
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedFilesForConversion.includes(
                                filePath
                              )}
                              onChange={(e) =>
                                handleFileSelectionChange(
                                  filePath,
                                  e.target.checked
                                )
                              }
                              disabled={!isConvertibleFile}
                            />
                            <span>
                              {filePath} {isPLIFile || isVB6File ? "" : isCodedUIFile ? "(Coded UI)" : isCypressFile ? "(Cypress)" : ""}
                            </span>
                          </div>
                        );
                      });
                    })()}
                  </div>

                  <div className="convert-dropdown-footer">
                    <button
                      className="convert-dropdown-cancel-btn"
                      onClick={() => setConvertDropdownOpen(false)}
                    >
                      Cancel
                    </button>
                    <button
                      className="convert-dropdown-convert-btn"
                      onClick={handleConvertToPython}
                      disabled={selectedFilesForConversion.length === 0}
                    >
                      To Python ({selectedFilesForConversion.length})
                    </button>
                    <button
                      className="convert-dropdown-convert-btn"
                      onClick={handleConvertToPlaywright}
                      disabled={selectedFilesForConversion.length === 0}
                      style={{ marginLeft: "8px", background: "#2ea043" }}
                    >
                      To Playwright ({selectedFilesForConversion.length})
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          {user && (
            <div className="user-info">
              <div
                className="user-profile"
                onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                title="User menu"
              >
                <img
                  src="/user.png"
                  alt="User avatar"
                  className="user-avatar"
                />
                <span className="user-name">{user.username}</span>
              </div>
              {userDropdownOpen && (
                <div className="user-dropdown">
                  <button
                    className="user-dropdown-item logout-button"
                    onClick={() => {
                      setUserDropdownOpen(false);
                      logout();
                    }}
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="main-content">
        {/* Sidebar */}
        <div
          ref={sidebarRef}
          className={`sidebar ${sidebarCollapsed ? "collapsed" : ""}`}
          style={{ width: sidebarCollapsed ? "56px" : `${sidebarWidth}px` }}
        >
          {!sessionId ? (
            <FileUpload onUpload={handleFileUpload} />
          ) : currentPage === "pli" ? (
            <FileExplorer
              fileTree={fileTree}
              onFileSelect={handleFileSelect}
              selectedFile={getCurrentSelectedFile()}
              fileType="PL/I"
              sidebarCollapsed={sidebarCollapsed}
              onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)}
              showAnalysisButtons={true}
              sessionId={sessionId}
              isImpactAnalyzing={isImpactAnalyzing}
              isDependencyAnalyzing={isDependencyAnalyzing}
              isUpdatingDocument={isUpdatingDocument}
              isAuthenticated={isAuthenticated}
              onRunAnalysis={handleRunAnalysis}
              onRunDependencyAnalysis={handleRunDependencyAnalysis}
              onUpdateDocument={handleUpdateDocument}
            />
          ) : currentPage === "python" ? (
            <FileExplorer
              fileTree={pythonFileTree}
              onFileSelect={handleFileSelect}
              selectedFile={getCurrentSelectedFile()}
              fileType="Python"
              sidebarCollapsed={sidebarCollapsed}
              onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)}
            />
          ) : (
            <FileExplorer
              fileTree={playwrightFileTree}
              onFileSelect={handleFileSelect}
              selectedFile={getCurrentSelectedFile()}
              fileType="Playwright"
              sidebarCollapsed={sidebarCollapsed}
              onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)}
            />
          )}

          {/* Resize Handle */}
          {!sidebarCollapsed && (
            <div
              className="sidebar-resize-handle"
              onMouseDown={startResizing}
            />
          )}
        </div>

        {/* Main Editor Area */}
        <div className="editor-area">
          {/* Tab Bar - Always show when there are tabs */}
          {getCurrentTabs().length > 0 && (
            <div className="tab-bar">
              {getCurrentTabs().map((tab) => (
                <div
                  key={tab.id}
                  className={`tab ${
                    getCurrentActiveTab() === tab.id ? "active" : ""
                  }`}
                  // style={{
                  //   backgroundColor:
                  //     getCurrentActiveTab() === tab.id ? "#ffffff" : "#f8f9fa",
                  //   borderRight: "1px solid #e0e0e0",

                  //   borderBottom:
                  //     getCurrentActiveTab() === tab.id
                  //       ? "2px solid #007ACC"
                  //       : "none",
                  // }}
                  onClick={() => {
                    setCurrentActiveTab(tab.id);
                    setCurrentSelectedFile(tab.path);
                  }}
                >
                  <span
                    className="tab-name"
                    style={{
                      flex: 1,
                      fontSize: "13px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {tab.isAnalysis && "📊 "}
                    {tab.isConversion && " "}
                    {tab.isDependencyAnalysis && "📊 "}
                    {tab.isPython && " "}
                    {tab.name}
                    {tab.modified && " ●"}
                  </span>
                  <button
                    className="tab-close"
                    style={{
                      background: "none",
                      border: "none",
                      color: "#999",
                      cursor: "pointer",
                      padding: "2px 4px",
                      marginLeft: "8px",
                      fontSize: "16px",
                      lineHeight: 1,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTabClose(tab.id);
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
              {getCurrentTabs().length > 1 && (
                <div
                  className="tab-actions"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginLeft: "auto",
                    padding: "0 8px",
                  }}
                >
                  {(currentPage === "python" || currentPage === "playwright") && getCurrentSelectedFile() && (
                    <button
                      className="close-all-tabs"
                      onClick={() => handleDownloadFile(
                        getCurrentSelectedFile(),
                        currentPage === "python" ? "python" : "playwright"
                      )}
                      title={`Download ${getCurrentSelectedFile()}`}
                      style={{ marginRight: "8px", background: "#0d6efd" }}
                    >
                      Download File
                    </button>
                  )}
                  {(currentPage === "python" || currentPage === "playwright") && (
                    <button
                      className="close-all-tabs"
                      onClick={() => handleDownloadAllAsZip(currentPage === "python" ? "python" : "playwright")}
                      title="Download all converted files as ZIP"
                      style={{ marginRight: "8px", background: "#2ea043" }}
                    >
                      Download All ZIP
                    </button>
                  )}
                  <button
                    className="close-all-tabs"
                    onClick={() => {
                      setCurrentTabs([]);
                      setCurrentActiveTab(null);
                      setCurrentSelectedFile(null);
                    }}
                    title="Close all tabs"
                  >
                    Close All
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Editor Content */}
          <div className="editor-content">
            {getCurrentActiveTab() ? (
              (() => {
                const activeTabData = getCurrentTabs().find(
                  (tab) => tab.id === getCurrentActiveTab()
                );

                // Safety check - if activeTabData is undefined, show error
                if (!activeTabData) {
                  // Active tab not found - this should not happen in normal operation
                  return (
                    <div className="welcome-screen">
                      <h2>Tab Not Found</h2>
                      <p>
                        The selected tab could not be found. Please select
                        another tab.
                      </p>
                    </div>
                  );
                }

                if (activeTabData.isAnalysis) {
                  return (
                    <AnalysisResults
                      analysisData={activeTabData.content}
                      sessionId={sessionId}
                    />
                  );
                } else if (activeTabData.isConversion) {
                  return (
                    <AnalysisResults
                      analysisData={activeTabData.content}
                      sessionId={sessionId}
                      isConversion={true}
                    />
                  );
                } else if (activeTabData.isDependencyAnalysis) {
                  return (
                    <DependencyAnalysisTab
                      analysisData={activeTabData.content}
                      sessionId={sessionId}
                    />
                  );
                } else {
                  return (
                    <CodeEditor
                      key={activeTabData.id}
                      content={activeTabData.content || ""}
                      filePath={activeTabData.path}
                      onSave={handleFileSave}
                      sessionId={sessionId}
                      showDownload={currentPage === "python" || currentPage === "playwright"}
                      onDownloadFile={
                        (currentPage === "python" || currentPage === "playwright")
                          ? () => handleDownloadFile(
                              activeTabData.path,
                              currentPage === "python" ? "python" : "playwright"
                            )
                          : undefined
                      }
                      onDownloadAll={
                        (currentPage === "python" || currentPage === "playwright")
                          ? () => handleDownloadAllAsZip(
                              currentPage === "python" ? "python" : "playwright"
                            )
                          : undefined
                      }
                      onChange={(newContent) => {
                        // Update the tab content when user types
                        const currentTabs = getCurrentTabs();
                        setCurrentTabs(
                          currentTabs.map((tab) =>
                            tab.id === activeTabData.id
                              ? { ...tab, content: newContent, modified: true }
                              : tab
                          )
                        );
                      }}
                    />
                  );
                }
              })()
            ) : (
              <div className="welcome-screen">
                <img
                  src="/welcome-screen.svg"
                  style={{
                    width: "100px",
                    height: "auto",
                    marginBottom: "20px",
                  }}
                  alt="welcome-screen"
                  className="auth-modal-logo"
                />
                <h2 style={{ textAlign: "left" }}>
                  Welcome to Code Editor
                </h2>
                {currentPage === "pli" ? (
                  <div>
                    <p>Upload code files or ZIP archive to get started</p>
                    <p
                      style={{
                        color: "#666",
                        fontSize: "14px",
                        marginTop: "4px",
                        textAlign: "left",
                      }}
                    >
                      Use the "Convert" button to convert code files
                      to Python or Playwright
                    </p>
                  </div>
                ) : currentPage === "python" ? (
                  <div>
                    <p>Python files converted from PL/I or VB6 code</p>
                    {Object.keys(pythonFiles).length === 0 ? (
                      <div
                        style={{
                          color: "#666",
                          fontStyle: "italic",
                          marginTop: "4px",
                        }}
                      >
                        <p>No Python files available.</p>
                        <p>Switch to Source page and convert some PL/I or VB6 files first.</p>
                      </div>
                    ) : (
                      <div style={{ color: "#28a745", marginTop: "20px" }}>
                        <p>
                          {Object.keys(pythonFiles).length} Python files
                          ready for editing
                        </p>
                        <p style={{ fontSize: "14px", color: "#666" }}>
                          Select files from the sidebar to start editing
                        </p>
                        <div style={{ marginTop: "16px", display: "flex", gap: "10px" }}>
                          <button
                            onClick={() => handleDownloadAllAsZip("python")}
                            style={{
                              padding: "8px 16px",
                              background: "#0d6efd",
                              color: "#fff",
                              border: "none",
                              borderRadius: "6px",
                              cursor: "pointer",
                              fontSize: "13px",
                            }}
                          >
                            Download All as ZIP
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <p>Playwright tests converted from Coded UI scripts</p>
                    {Object.keys(playwrightFiles).length === 0 ? (
                      <div
                        style={{
                          color: "#666",
                          fontStyle: "italic",
                          marginTop: "4px",
                        }}
                      >
                        <p>No Playwright files available.</p>
                        <p>Switch to Source page and convert some Coded UI (.cs) files first.</p>
                      </div>
                    ) : (
                      <div style={{ color: "#28a745", marginTop: "20px" }}>
                        <p>
                          {Object.keys(playwrightFiles).length} Playwright test files
                          ready for editing
                        </p>
                        <p style={{ fontSize: "14px", color: "#666" }}>
                          Select files from the sidebar to start editing
                        </p>
                        <div style={{ marginTop: "16px", display: "flex", gap: "10px" }}>
                          <button
                            onClick={() => handleDownloadAllAsZip("playwright")}
                            style={{
                              padding: "8px 16px",
                              background: "#2ea043",
                              color: "#fff",
                              border: "none",
                              borderRadius: "6px",
                              cursor: "pointer",
                              fontSize: "13px",
                            }}
                          >
                            Download All as ZIP
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* AI Assistant Panel */}
        <div
          ref={aiPanelRef}
          className={`ai-panel ${aiPanelCollapsed ? "collapsed" : ""}`}
          style={{ width: aiPanelCollapsed ? "0px" : `${aiPanelWidth}px` }}
        >
          {/* AI Panel Resize Handle */}
          {!aiPanelCollapsed && (
            <div
              className="ai-panel-resize-handle"
              onMouseDown={startResizingAiPanel}
            />
          )}
          <AIAssistant
            sessionId={sessionId}
            selectedFile={aiAssistantSelectedFile}
            fileContent={aiAssistantFileContent}
            files={aiAssistantFiles}
            originalFiles={aiAssistantOriginalFiles}
            analysisResults={analysisResults}
            chatHistory={chatHistory}
            isLoading={isLoading}
            isAvailable={isAvailable}
            onSendMessage={handleSendMessage}
            onClearChat={clearChat}
            onOpenExtendedAI={handleOpenAiOverlay}
            onCollapse={handleCollapseAiPanel}
          />
        </div>
      </div>

      {/* AI Panel Floating Button - Shows when panel is collapsed */}
      {aiPanelCollapsed && !floatingButtonClosing && (
        <button
          className="ai-floating-button"
          onClick={handleOpenAiPanelFromFloatingButton}
          title="Open AI Assistant"
          style={{
            background: "linear-gradient(to right, #AC2CBD, #08C7CB)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "12px",
            border: "none",
            borderRadius: "50%",
            cursor: "pointer",
          }}
        >
          <img src="/btn-logo.svg" alt="AI Assistant" width="24" height="24" />
        </button>
      )}

      {/* Floating button disappearance animation placeholder */}
      {aiPanelCollapsed && floatingButtonClosing && (
        <div
          className="ai-floating-button"
          style={{
            background: "linear-gradient(to right, #AC2CBD, #08C7CB)",
            animation: "slideDown 0.4s cubic-bezier(0.4, 0, 0.2, 1) forwards",
            pointerEvents: "none",
          }}
        >
          <img src="/btn-logo.svg" alt="AI Assistant" width="24" height="24" />
        </div>
      )}

      {/* AI Assistant Full-Screen Overlay */}
      {aiOverlayOpen && (
        <div className="ai-overlay">
          <div
            className="ai-overlay-backdrop"
            onClick={handleCloseAiOverlay}
          ></div>
          <div className="ai-overlay-content">
            <AIAssistant
              sessionId={sessionId}
              selectedFile={aiAssistantSelectedFile}
              fileContent={aiAssistantFileContent}
              files={aiAssistantFiles}
              originalFiles={aiAssistantOriginalFiles}
              analysisResults={analysisResults}
              isFullScreen={true}
              chatHistory={chatHistory}
              isLoading={isLoading}
              isAvailable={isAvailable}
              onSendMessage={handleSendMessage}
              onClearChat={clearChat}
              onClose={handleCloseAiOverlay}
            />
          </div>
        </div>
      )}

      {/* Authentication Modal */}
      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        initialMode="login"
      />
    </div>
  );
}

// Main App component with AuthProvider
function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
